import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Section, Ticker, pct, tone } from "../components/UniverseBacktestParts";

const GRADE_CLS = {
  Promising: "bg-emerald-500/15 text-emerald-600",
  Baseline: "bg-neutral-500/15 text-neutral-600",
};
const gradeCls = (g) => GRADE_CLS[g] || (g.startsWith("Weak") ? "bg-amber-500/15 text-amber-600" : "bg-red-500/15 text-red-600");

function SetupTable({ setups, side, ex }) {
  const rows = setups.filter((s) => s.side === side);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[820px]" data-testid={`es-table-${side}-${ex}`}>
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
            <th className="py-1.5 pr-3 font-medium">Setup</th><th className="py-1.5 pr-3 font-medium text-right">Trades</th>
            <th className="py-1.5 pr-3 font-medium text-right">Win rate</th><th className="py-1.5 pr-3 font-medium text-right">Avg</th>
            <th className="py-1.5 pr-3 font-medium text-right">Edge vs rest</th><th className="py-1.5 pr-3 font-medium text-right">1st / 2nd half</th>
            <th className="py-1.5 font-medium">Verdict</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const st = s.exits[ex];
            return (
              <tr key={s.code} className="border-t border-mb-border align-top" data-testid={`es-row-${s.code}-${ex}`}>
                <td className="py-2 pr-3"><div className="font-semibold text-mb-ink">{s.name}</div><div className="text-[11px] text-neutral-500">{s.rule}</div></td>
                <td className="py-2 pr-3 text-right font-mono-data">{st.trades}</td>
                <td className="py-2 pr-3 text-right font-mono-data">{st.win_rate}%</td>
                <td className={`py-2 pr-3 text-right font-mono-data ${tone(st.avg_return_pct)}`}>{pct(st.avg_return_pct, 2)}</td>
                <td className="py-2 pr-3 text-right font-mono-data">{st.edge_pct === undefined || st.edge_pct === null ? "—" : <span className={tone(st.edge_pct)}>{pct(st.edge_pct, 2)} <span className="text-neutral-500">t={st.t_stat}</span></span>}</td>
                <td className="py-2 pr-3 text-right font-mono-data text-xs">{pct(st.first_half.avg, 2)} / {pct(st.second_half.avg, 2)}</td>
                <td className="py-2"><span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${gradeCls(st.grade)}`}>{st.grade}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BestTrades({ setups }) {
  const lead = setups.filter((s) => s.side === "before" && s.code !== "B0")
    .map((s) => ({ s, st: s.exits.big }))
    .sort((a, b) => (b.st.t_stat || 0) - (a.st.t_stat || 0))[0];
  if (!lead || !lead.st.sample?.length) return null;
  return (
    <p className="mt-4 text-xs text-neutral-600" data-testid="es-best-trades">
      Biggest wins for {lead.s.name} (big-name exits):{" "}
      {lead.st.sample.slice(0, 6).map((t, i) => (
        <span key={`${t.symbol}-${t.report_date}`}>{i ? " · " : ""}<Ticker sym={t.symbol} /> {pct(t.ret, 1)}</span>
      ))}
    </p>
  );
}

export default function AdminEarningsSignal() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/backtests/earnings-signal").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="min-h-screen bg-mb-sand flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-mb-emerald" /></div>;
  return (
    <div className="bg-mb-sand min-h-screen" data-testid="es-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        <Link to="/admin/backtests/universe-expansion" className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-mb-ink mb-6" data-testid="es-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to the $500M+ backtest
        </Link>
        <div className="uppercase tracking-widest text-[11px] text-mb-violet font-bold">Analyst desk · Backtest</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2 text-mb-ink">Big names around earnings</h1>
        {!data.setups ? (
          <p className="mt-8 text-sm text-neutral-600" data-testid="es-empty">No results yet.</p>
        ) : (
          <>
            <p className="mt-3 text-neutral-600 max-w-2xl leading-relaxed">
              {data.events.toLocaleString()} earnings reports from stocks over $30B, {data.window.start} → {data.window.end}. Tested before the report and the day after, with the live exits and tighter big-name exits.
            </p>
            <div className={`mt-8 rounded-lg border-2 p-6 ${data.promising?.length ? "border-emerald-500 bg-emerald-500/10" : "border-amber-500 bg-amber-500/10"}`} data-testid="es-headline">
              <p className="text-base md:text-lg font-semibold text-mb-ink">{data.headline}</p>
              <p className="mt-2 text-xs text-neutral-600">Nothing is switched on from this test. One year, several setups: anything below t ≥ 2 is a lead to keep watching, not a rule.</p>
            </div>
            {Object.entries(data.exits).map(([ex, label]) => (
              <React.Fragment key={ex}>
                <Section title={`Before the report · ${label}`} sub="Buy the close of the session before the report date." testId={`es-before-${ex}`}>
                  <SetupTable setups={data.setups} side="before" ex={ex} />
                  {ex === "big" && <BestTrades setups={data.setups} />}
                </Section>
                <Section title={`Day after · ${label}`} sub="Buy the close of the session after the report date." testId={`es-after-${ex}`}>
                  <SetupTable setups={data.setups} side="after" ex={ex} />
                </Section>
              </React.Fragment>
            ))}
            <Section title="Method" testId="es-method">
              <ul className="space-y-2 text-sm text-neutral-700 list-disc pl-5">{data.method.map((m) => <li key={m}>{m}</li>)}</ul>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
